import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { DashboardAPI, DashboardCountRow, DashboardPeriod, DashboardSalesRow, DashboardSummary, DashboardTotals } from '../services/api';
import { BUSINESS_DAY_HINT } from '../utils/businessDay';

// Numbers come from GET /dashboard/summary, which decides on the server what each
// role sees (everything / team / own). All dates are business days (5 AM to 5 AM
// Pakistan time) as "YYYY-MM-DD"
// strings, read as UTC here so the browser's time zone can't shift the day.
const parseDay = (s: string) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};
const fmtDay = (s: string, opts: Intl.DateTimeFormatOptions = {}) =>
  parseDay(s).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', ...opts });
const money = (n: number) =>
  (n < 0 ? '-$' : '$') + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const whole = (n: number) => n.toLocaleString('en-US');
const compact = (n: number) =>
  n >= 1000 ? `$${(n / 1000).toLocaleString('en-US', { maximumFractionDigits: 1 })}K` : `$${Math.round(n)}`;

const PERIODS: { key: DashboardPeriod; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'week', label: 'This week' },
  { key: 'last_week', label: 'Last week' },
  { key: 'month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'custom', label: 'Custom' },
];
// Queue order for the workload tiles and table columns.
const STATUS_LABELS: Record<string, string> = {
  PENDING_ASSIGNMENT: 'Waiting for assignment',
  ASSIGNED: 'Assigned',
  IN_PROCESS: 'In process',
  DOCUMENTS_REQUIRED: 'Documents required',
  UNDER_REVIEW: 'Under review',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  CHARGEBACK: 'Chargeback',
};
const STATUS_ORDER = Object.keys(STATUS_LABELS);
const MAX_DAILY_BARS = 92; // longer custom ranges chart by month

const Tile: React.FC<{ label: string; value: string; note?: string }> = ({ label, value, note }) => (
  <div className="dash-card dash-tile">
    <div className="dash-tile-label">{label}</div>
    <div className="dash-tile-value">{value}</div>
    {note && <div className="dash-tile-note">{note}</div>}
  </div>
);

const SalesTable: React.FC<{ rows: DashboardSalesRow[]; firstHead: string }> = ({ rows, firstHead }) => {
  if (!rows.length) return <div className="dash-empty">No sales in this period.</div>;
  const max = Math.max(...rows.map((r) => Math.max(r.net, 0)), 1);
  const sum = (k: 'sales' | 'amount' | 'voids' | 'void_amount' | 'refunds' | 'refund_amount' | 'net') =>
    rows.reduce((a, r) => a + r[k], 0);
  const pair = (n: number, amount: number) => (n ? `${whole(n)} / ${money(amount)}` : '0');
  return (
    <div className="dash-tablewrap">
      <table className="dash-table">
        <thead>
          <tr>
            <th>{firstHead}</th><th>Sales</th><th>Amount</th><th>Voids</th><th>Refunds</th><th>Net</th><th aria-hidden="true" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.name}>
              <td>{r.name}</td>
              <td>{whole(r.sales)}</td>
              <td>{money(r.amount)}</td>
              <td>{pair(r.voids, r.void_amount)}</td>
              <td>{pair(r.refunds, r.refund_amount)}</td>
              <td>{money(r.net)}</td>
              <td className="dash-barcell"><div className="dash-bar" style={{ width: `${(Math.max(r.net, 0) / max) * 100}%` }} /></td>
            </tr>
          ))}
          {rows.length > 1 && (
            <tr className="dash-total">
              <td>Total</td>
              <td>{whole(sum('sales'))}</td>
              <td>{money(sum('amount'))}</td>
              <td>{pair(sum('voids'), sum('void_amount'))}</td>
              <td>{pair(sum('refunds'), sum('refund_amount'))}</td>
              <td>{money(sum('net'))}</td>
              <td />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

interface ChartPoint extends DashboardTotals { key: string; axis: string; tip: string; }

const SalesChart: React.FC<{ points: ChartPoint[] }> = ({ points }) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(900);
  const [active, setActive] = useState<number | null>(null);
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const measure = () => setW(Math.max(el.clientWidth, 280));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const H = 240;
  const m = { t: 22, r: 6, b: 26, l: 44 };
  const pw = W - m.l - m.r;
  const ph = H - m.t - m.b;
  const max = Math.max(...points.map((p) => p.amount), 0);
  const steps = [100, 200, 500, 1000, 2000, 2500, 5000, 10000, 20000, 25000, 50000, 100000, 200000, 500000];
  const step = steps.find((s) => max / s <= 4) || 1000000;
  const top = Math.max(Math.ceil(max / step), 1) * step;
  const y = (v: number) => m.t + ph - (v / top) * ph;
  const band = pw / points.length;
  const thick = Math.min(24, Math.max(band - 2, 1));
  const labelEvery = Math.max(Math.ceil(points.length / (W < 520 ? 3 : 6)), 1);
  const maxI = points.reduce((b, p, i) => (p.amount > points[b].amount ? i : b), 0);
  const ticks: number[] = [];
  for (let v = 0; v <= top + 0.001; v += step) ticks.push(v);
  const activePoint = active != null ? points[active] : null;

  return (
    <div className="dash-chart" ref={boxRef}>
      <svg role="img" aria-label="Sales amount over the selected dates" width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
        {ticks.map((v) => (
          <g key={v}>
            <line x1={m.l} x2={W - m.r} y1={y(v)} y2={y(v)} stroke={v === 0 ? 'var(--border-secondary)' : 'var(--border-primary)'} />
            <text x={m.l - 8} y={y(v) + 4} textAnchor="end" fontSize="12" fill="var(--text-secondary)" style={{ fontVariantNumeric: 'tabular-nums' }}>{compact(v)}</text>
          </g>
        ))}
        {points.map((p, i) => {
          const cx = m.l + band * i + band / 2;
          const h = (p.amount / top) * ph;
          const x0 = cx - thick / 2;
          const yTop = m.t + ph - h;
          const r = Math.min(4, thick / 2, h);
          // Only the last label of a crowded axis is pinned to the edge; with few bars each sits under its bar.
          const pinRight = i === points.length - 1 && band < 64;
          return (
            <g key={p.key}>
              {h > 0 && (
                <path fill="var(--dash-series)" d={`M${x0} ${m.t + ph}V${yTop + r}Q${x0} ${yTop} ${x0 + r} ${yTop}H${x0 + thick - r}Q${x0 + thick} ${yTop} ${x0 + thick} ${yTop + r}V${m.t + ph}Z`} />
              )}
              {(points.length - 1 - i) % labelEvery === 0 && (
                <text x={pinRight ? W - m.r : cx} y={H - 8} textAnchor={pinRight ? 'end' : 'middle'} fontSize="12" fill="var(--text-secondary)">{p.axis}</text>
              )}
              {i === maxI && p.amount > 0 && (
                <text x={Math.min(Math.max(cx, m.l + 22), W - 26)} y={yTop - 6} textAnchor="middle" fontSize="12" fontWeight="600" fill="var(--text-primary)">{compact(p.amount)}</text>
              )}
              <rect
                x={m.l + band * i} y={m.t} width={band} height={ph} fill="transparent" tabIndex={0}
                aria-label={`${p.tip}: ${whole(p.sales)} sales, ${money(p.amount)} charged, ${whole(p.voids)} voids, ${whole(p.refunds)} refunds, ${money(p.net)} net`}
                onMouseEnter={() => setActive(i)} onMouseLeave={() => setActive(null)}
                onFocus={() => setActive(i)} onBlur={() => setActive(null)}
              />
            </g>
          );
        })}
      </svg>
      {activePoint && active != null && (
        <div className="dash-tip" style={{ left: Math.min(Math.max(m.l + band * active + band / 2, 100), W - 100), top: Math.max(y(activePoint.amount) - 8, 112) }}>
          <strong>{activePoint.tip}</strong>
          <div>{whole(activePoint.sales)} sales, {money(activePoint.amount)}</div>
          <div>Voids: {whole(activePoint.voids)}, {money(activePoint.void_amount)}</div>
          <div>Refunds: {whole(activePoint.refunds)}, {money(activePoint.refund_amount)}</div>
          <div><strong>Net: {money(activePoint.net)}</strong></div>
        </div>
      )}
    </div>
  );
};

// Pakistan hours in business-day order (the day starts at 5 AM PKT), so the
// evening calling shift reads as one block.
const HOUR_ORDER = Array.from({ length: 24 }, (_, i) => (i + 5) % 24);
const hourLabel = (h: number) => `${h % 12 || 12} ${h < 12 ? 'AM' : 'PM'}`;

const HourChart: React.FC<{ hours: { hour: number; sales: number; amount: number }[] }> = ({ hours }) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(900);
  const [active, setActive] = useState<number | null>(null);
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const measure = () => setW(Math.max(el.clientWidth, 280));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const byHour = new Map(hours.map((h) => [h.hour, h]));
  const points = HOUR_ORDER.map((h) => byHour.get(h) || { hour: h, sales: 0, amount: 0 });
  const H = 220;
  const m = { t: 22, r: 6, b: 26, l: 36 };
  const pw = W - m.l - m.r;
  const ph = H - m.t - m.b;
  const max = Math.max(...points.map((p) => p.sales), 0);
  const steps = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000];
  const step = steps.find((s) => max / s <= 4) || 2000;
  const top = Math.max(Math.ceil(max / step), 1) * step;
  const y = (v: number) => m.t + ph - (v / top) * ph;
  const band = pw / points.length;
  const thick = Math.min(24, Math.max(band - 4, 1));
  const labelEvery = W < 520 ? 6 : 3;
  const maxI = points.reduce((b, p, i) => (p.sales > points[b].sales ? i : b), 0);
  const ticks: number[] = [];
  for (let v = 0; v <= top; v += step) ticks.push(v);
  const activePoint = active != null ? points[active] : null;

  return (
    <div className="dash-chart" ref={boxRef}>
      <svg role="img" aria-label="Number of sales by hour of the day, Pakistan time" width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
        {ticks.map((v) => (
          <g key={v}>
            <line x1={m.l} x2={W - m.r} y1={y(v)} y2={y(v)} stroke={v === 0 ? 'var(--border-secondary)' : 'var(--border-primary)'} />
            <text x={m.l - 8} y={y(v) + 4} textAnchor="end" fontSize="12" fill="var(--text-secondary)" style={{ fontVariantNumeric: 'tabular-nums' }}>{whole(v)}</text>
          </g>
        ))}
        {points.map((p, i) => {
          const cx = m.l + band * i + band / 2;
          const h = (p.sales / top) * ph;
          const x0 = cx - thick / 2;
          const yTop = m.t + ph - h;
          const r = Math.min(4, thick / 2, h);
          return (
            <g key={p.hour}>
              {h > 0 && (
                <path fill="var(--dash-series)" d={`M${x0} ${m.t + ph}V${yTop + r}Q${x0} ${yTop} ${x0 + r} ${yTop}H${x0 + thick - r}Q${x0 + thick} ${yTop} ${x0 + thick} ${yTop + r}V${m.t + ph}Z`} />
              )}
              {i % labelEvery === 0 && (
                <text x={cx} y={H - 8} textAnchor="middle" fontSize="12" fill="var(--text-secondary)">{hourLabel(p.hour)}</text>
              )}
              {i === maxI && p.sales > 0 && (
                <text x={cx} y={yTop - 6} textAnchor="middle" fontSize="12" fontWeight="600" fill="var(--text-primary)">{whole(p.sales)}</text>
              )}
              <rect
                x={m.l + band * i} y={m.t} width={band} height={ph} fill="transparent" tabIndex={0}
                aria-label={`${hourLabel(p.hour)} to ${hourLabel((p.hour + 1) % 24)}: ${whole(p.sales)} sales, ${money(p.amount)}`}
                onMouseEnter={() => setActive(i)} onMouseLeave={() => setActive(null)}
                onFocus={() => setActive(i)} onBlur={() => setActive(null)}
              />
            </g>
          );
        })}
      </svg>
      {activePoint && active != null && (
        <div className="dash-tip" style={{ left: Math.min(Math.max(m.l + band * active + band / 2, 90), W - 90), top: Math.max(y(activePoint.sales) - 8, 60) }}>
          <strong>{hourLabel(activePoint.hour)} to {hourLabel((activePoint.hour + 1) % 24)} PKT</strong>
          <div>{whole(activePoint.sales)} sales, {money(activePoint.amount)}</div>
        </div>
      )}
    </div>
  );
};

const CountBars: React.FC<{ rows: DashboardCountRow[]; label: string }> = ({ rows, label }) => {
  const [active, setActive] = useState<string | null>(null);
  if (!rows.length) return <div className="dash-empty">No sales in this period.</div>;
  const max = Math.max(...rows.map((r) => r.sales), 1);
  return (
    <ul className="dash-hbars" aria-label={label}>
      {rows.map((r) => (
        <li
          key={r.name} tabIndex={0} aria-label={`${r.name}: ${whole(r.sales)} sales, ${money(r.amount)}`}
          onMouseEnter={() => setActive(r.name)} onMouseLeave={() => setActive(null)}
          onFocus={() => setActive(r.name)} onBlur={() => setActive(null)}
        >
          <span className="dash-hbar-name" title={r.name}>{r.name}</span>
          <span className="dash-hbar-track">
            <span className={`dash-hbar${r.name === 'Not set' || r.name.startsWith('Other (') ? ' dash-hbar-muted' : ''}`} style={{ width: `${(r.sales / max) * 100}%` }} />
          </span>
          <span className="dash-hbar-value">{whole(r.sales)}</span>
          {active === r.name && <span className="dash-tip dash-hbar-tip">{whole(r.sales)} sales, {money(r.amount)}</span>}
        </li>
      ))}
    </ul>
  );
};

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [period, setPeriod] = useState<DashboardPeriod>('today');
  // Custom dates: what's typed vs what was last applied (the fetch follows `applied`).
  const [draft, setDraft] = useState({ from: '', to: '' });
  const [applied, setApplied] = useState({ from: '', to: '' });
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [shownPeriod, setShownPeriod] = useState<DashboardPeriod>('today'); // the period `data` is for
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);

  const load = useCallback(async (p: DashboardPeriod, from: string, to: string) => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const res = await DashboardAPI.getSummary(p, from, to);
      if (id === requestId.current) {
        setData(res);
        setShownPeriod(p);
        setUpdatedAt(new Date());
      }
    } catch (e: any) {
      if (id === requestId.current) setError(e?.message || 'Failed to load the dashboard');
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (period === 'custom' && !(applied.from && applied.to)) return; // waits for Apply
    load(period, applied.from, applied.to);
  }, [period, applied, load]);

  const choosePeriod = (p: DashboardPeriod) => {
    if (p === 'custom' && !draft.from && data) {
      // Start the date boxes on this month so far.
      setDraft({ from: `${data.today.slice(0, 7)}-01`, to: data.today });
    }
    setPeriod(p);
  };
  const draftInvalid = !draft.from || !draft.to || draft.from > draft.to;

  const points = useMemo<ChartPoint[]>(() => {
    const daily = data?.daily || [];
    if (daily.length <= MAX_DAILY_BARS) {
      return daily.map((d) => ({
        key: d.day,
        axis: fmtDay(d.day),
        tip: fmtDay(d.day, { weekday: 'short' }) + (d.day === data?.today ? ' so far' : ''),
        ...d,
      }));
    }
    const months = new Map<string, ChartPoint>();
    daily.forEach((d) => {
      const key = d.day.slice(0, 7);
      if (!months.has(key)) {
        const label = parseDay(`${key}-01`).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', year: 'numeric' });
        months.set(key, { key, axis: label, tip: label, sales: 0, amount: 0, voids: 0, void_amount: 0, refunds: 0, refund_amount: 0, net: 0 });
      }
      const m = months.get(key)!;
      m.sales += d.sales;
      m.amount += d.amount;
      m.voids += d.voids;
      m.void_amount += d.void_amount;
      m.refunds += d.refunds;
      m.refund_amount += d.refund_amount;
      m.net += d.net;
    });
    return Array.from(months.values());
  }, [data]);
  const byMonth = (data?.daily?.length || 0) > MAX_DAILY_BARS;

  const rangeLabel = data
    ? data.range.from === data.range.to ? fmtDay(data.range.from) : `${fmtDay(data.range.from)} to ${fmtDay(data.range.to)}`
    : '';
  const showFilters = !!data?.show_sales;
  const totals = data?.totals;
  const previous = data?.previous;
  const compareLabel = shownPeriod === 'week' ? 'the same days last week'
    : shownPeriod === 'month' ? 'the same days last month'
    : shownPeriod === 'yesterday' ? 'the day before'
    : shownPeriod === 'last_week' ? 'the week before'
    : shownPeriod === 'last_month' ? 'the month before'
    : previous ? `${fmtDay(previous.from)} to ${fmtDay(previous.to)}` : '';
  const delta = totals && previous && previous.net > 0 ? ((totals.net - previous.net) / previous.net) * 100 : null;

  const work = data?.workload;
  const workTiles = (work?.by_status || [])
    .filter((s) => work?.scope === 'all' || s.status !== 'PENDING_ASSIGNMENT')
    .sort((a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status));
  const workColumns = STATUS_ORDER.filter((s) => (work?.processors || []).some((p) => p.statuses[s]));
  const workMax = Math.max(...(work?.processors || []).map((p) => p.total), 1);

  return (
    <DashboardLayout>
      <div className={`dash${loading && data ? ' dash-loading' : ''}`}>
        <div className="dash-head">
          <div>
            <h1>Dashboard</h1>
            <p>{data?.show_sales ? `${rangeLabel}, days run 5 AM to 5 AM Pakistan time (PKT)` : 'Your current processing queue'}</p>
          </div>
          <div className="dash-refresh">
            {updatedAt && (
              <span>Updated {updatedAt.toLocaleTimeString('en-US', { timeZone: 'Asia/Karachi', hour: 'numeric', minute: '2-digit' })} PKT</span>
            )}
            <button type="button" disabled={loading} onClick={() => load(shownPeriod, applied.from, applied.to)}>
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="dash-filters">
            <div className="dash-seg" role="group" aria-label="Period">
              {PERIODS.map((p) => (
                <button key={p.key} type="button" aria-pressed={period === p.key} onClick={() => choosePeriod(p.key)}>{p.label}</button>
              ))}
            </div>
            {period === 'custom' && (
              <form className="dash-custom" onSubmit={(e) => { e.preventDefault(); if (!draftInvalid) setApplied({ ...draft }); }}>
                <label>From<input type="date" title={BUSINESS_DAY_HINT} value={draft.from} max={data?.today} onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))} /></label>
                <label>To<input type="date" title={BUSINESS_DAY_HINT} value={draft.to} max={data?.today} onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))} /></label>
                <button type="submit" disabled={draftInvalid}>Apply</button>
                {draft.from && draft.to && draft.from > draft.to && <span className="dash-custom-error">From must be on or before To.</span>}
              </form>
            )}
          </div>
        )}

        {error && <div className="dash-error">{error}</div>}
        {!data && loading && <div className="dash-empty">Loading dashboard…</div>}

        {data?.show_sales && totals && (
          <>
            <div className="dash-card dash-hero">
              <div className="dash-tile-label">Net sales{shownPeriod === 'today' ? ' (day still in progress)' : ''}</div>
              <div className="dash-hero-value">{money(totals.net)}</div>
              {previous && (
                <div className="dash-tile-note">
                  {delta != null ? (
                    <>
                      <span className={delta >= 0 ? 'dash-up' : 'dash-down'}>{delta >= 0 ? '▲ +' : '▼ '}{delta.toFixed(1)}%</span>
                      {` vs ${compareLabel} (${money(previous.net)})`}
                    </>
                  ) : `Nothing to compare in ${compareLabel}`}
                </div>
              )}
            </div>
            <div className="dash-tiles">
              <Tile label="Sales" value={whole(totals.sales)} note={`${whole(totals.sales - totals.voids - totals.refunds)} net after voids and refunds`} />
              <Tile label="Amount charged" value={money(totals.amount)} />
              <Tile label="Voids" value={whole(totals.voids)} note={money(totals.void_amount)} />
              <Tile label="Refunds" value={whole(totals.refunds)} note={money(totals.refund_amount)} />
            </div>

            <section>
              <h2>{byMonth ? 'Sales per month' : 'Sales per day'}</h2>
              <p className="dash-sub">
                Amount charged {shownPeriod === 'custom' ? 'in the selected dates' : shownPeriod === 'last_month' ? 'last month' : 'over the last 30 days'}, before voids and refunds. Hover over or tap a bar for its voids, refunds and net.
              </p>
              <div className="dash-card">
                <SalesChart points={points} />
                <details className="dash-details">
                  <summary>Show as table</summary>
                  <div className="dash-tablewrap dash-daytable">
                    <table className="dash-table">
                      <thead><tr><th>{byMonth ? 'Month' : 'Day'}</th><th>Sales</th><th>Amount</th><th>Voids</th><th>Refunds</th><th>Net</th></tr></thead>
                      <tbody>
                        {[...points].reverse().map((p) => (
                          <tr key={p.key}><td>{p.tip}</td><td>{whole(p.sales)}</td><td>{money(p.amount)}</td><td>{p.voids ? `${whole(p.voids)} / ${money(p.void_amount)}` : '0'}</td><td>{p.refunds ? `${whole(p.refunds)} / ${money(p.refund_amount)}` : '0'}</td><td>{money(p.net)}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              </div>
            </section>

            <section>
              <h2>Sales by time of day</h2>
              <p className="dash-sub">
                Number of sales in the selected dates by the hour the agent created the payment link (the call that closed the sale), Pakistan time. Direct card sales use their charge time. Hover over or tap a bar for the amount.
              </p>
              <div className="dash-card">
                {totals.sales ? <HourChart hours={data.by_hour || []} /> : <div className="dash-empty">No sales in this period.</div>}
              </div>
            </section>

            <div className="dash-grid2">
              <section>
                <h2>Sales by state</h2>
                <p className="dash-sub">Sale state, busiest 10 states. Hover over or tap a bar for the amount.</p>
                <div className="dash-card"><CountBars rows={data.by_state || []} label="Number of sales by state" /></div>
              </section>
              <section>
                <h2>Sales by domain</h2>
                <p className="dash-sub">A sale with more than one domain counts under each. Hover over or tap a bar for the amount.</p>
                <div className="dash-card"><CountBars rows={data.by_domain || []} label="Number of sales by domain" /></div>
              </section>
              <section>
                <h2>Sales by TFN</h2>
                <p className="dash-sub">Toll-free number on the sale, last 4 digits. Hover over or tap a bar for the amount.</p>
                <div className="dash-card"><CountBars rows={data.by_tfn || []} label="Number of sales by TFN" /></div>
              </section>
            </div>

            {data.show_agents && (
              <section>
                <h2>{user?.role === 'sales_team_lead' ? 'Team performance' : 'Agent performance'}</h2>
                <p className="dash-sub">Credited agent, highest net sales first.</p>
                <div className="dash-card"><SalesTable rows={data.agents || []} firstHead="Agent" /></div>
              </section>
            )}

            {data.show_processors && (
              <section>
                <h2>Sale by processor</h2>
                <p className="dash-sub">Payment processor that took the money.</p>
                <div className="dash-card"><SalesTable rows={data.processors || []} firstHead="Processor" /></div>
              </section>
            )}
          </>
        )}

        {data?.show_work && work && (
          <section>
            <h2>{work.scope === 'all' ? 'Processing workload' : work.scope === 'team' ? 'Team workload' : 'My workload'}</h2>
            <p className="dash-sub">Current queue right now.</p>
            <div className="dash-tiles">
              {workTiles.map((s) => <Tile key={s.status} label={STATUS_LABELS[s.status] || s.status} value={whole(s.count)} note={money(s.amount)} />)}
              <Tile label="Marked as deposit" value={whole(work.deposits)} />
            </div>
            {work.scope !== 'own' && work.processors.length > 0 && (
              <div className="dash-card dash-tablewrap dash-mt">
                <table className="dash-table">
                  <thead>
                    <tr>
                      <th>Processor</th>
                      {workColumns.map((s) => <th key={s}>{s === 'PENDING_ASSIGNMENT' ? 'Not started' : STATUS_LABELS[s]}</th>)}
                      <th>Deposits</th><th>Total</th><th aria-hidden="true" />
                    </tr>
                  </thead>
                  <tbody>
                    {work.processors.map((p) => (
                      <tr key={p.name}>
                        <td>{p.name}</td>
                        {workColumns.map((s) => <td key={s}>{whole(p.statuses[s] || 0)}</td>)}
                        <td>{whole(p.deposits)}</td>
                        <td>{whole(p.total)}</td>
                        <td className="dash-barcell"><div className="dash-bar" style={{ width: `${(p.total / workMax) * 100}%` }} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {data?.show_approvals && data.approvals && (
          <section>
            <h2>Waiting for approval</h2>
            <p className="dash-sub">Right now.</p>
            <div className="dash-tiles">
              <Tile label="Sales" value={whole(data.approvals.sales)} note={data.approvals.sales ? money(data.approvals.sales_amount) : 'Nothing waiting'} />
              <Tile label="Payment links" value={whole(data.approvals.links)} note={data.approvals.links ? money(data.approvals.links_amount) : 'Nothing waiting'} />
            </div>
          </section>
        )}
      </div>

      <style>{`
        .dash { --dash-series: var(--primary); max-width: 1200px; margin: 0 auto; color: var(--text-primary); text-align: left; transition: opacity .15s; }
        [data-theme='dark'] .dash { --dash-series: #7478f2; }
        .dash-loading { opacity: .6; }
        .dash-head { display: flex; flex-wrap: wrap; gap: 10px 20px; align-items: flex-start; justify-content: space-between; }
        .dash-refresh { display: flex; align-items: center; gap: 10px; font-size: .8125rem; color: var(--text-secondary); }
        .dash-refresh button { font: inherit; font-size: .875rem; font-weight: 600; color: var(--text-primary); background: var(--bg-card); border: 1px solid var(--border-secondary); border-radius: 7px; padding: 7px 14px; cursor: pointer; }
        .dash-refresh button:hover:not(:disabled) { background: var(--bg-tertiary); }
        .dash-refresh button:disabled { opacity: .6; cursor: default; }
        .dash-head h1 { margin: 0; font-size: 1.5rem; font-weight: 600; }
        .dash-head p { margin: 4px 0 0; color: var(--text-secondary); }
        .dash-filters { display: flex; flex-wrap: wrap; gap: 12px 20px; align-items: center; margin: 18px 0; }
        .dash-seg { display: inline-flex; flex-wrap: wrap; max-width: 100%; border: 1px solid var(--border-secondary); border-radius: 8px; overflow: hidden; background: var(--bg-card); }
        .dash-seg button { font: inherit; font-size: .875rem; color: var(--text-primary); background: none; border: 0; padding: 7px 14px; cursor: pointer; transition: none; }
        .dash-seg button + button { border-left: 1px solid var(--border-secondary); }
        .dash-seg button:hover { background: var(--bg-tertiary); }
        .dash-seg button[aria-pressed='true'], .dash-seg button[aria-pressed='true']:hover { background: var(--primary); color: var(--text-inverse); font-weight: 600; }
        .dash-custom { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; }
        .dash-custom label { display: inline-flex; gap: 6px; align-items: center; font-size: .8125rem; color: var(--text-secondary); }
        .dash-custom input { font: inherit; font-size: .875rem; color: var(--text-primary); background: var(--input-bg); border: 1px solid var(--input-border); border-radius: 7px; padding: 6px 8px; color-scheme: light; }
        [data-theme='dark'] .dash-custom input { color-scheme: dark; }
        .dash-custom button { font: inherit; font-size: .875rem; font-weight: 600; color: var(--text-inverse); background: var(--primary); border: 0; border-radius: 7px; padding: 7px 14px; cursor: pointer; }
        .dash-custom button:disabled { opacity: .5; cursor: not-allowed; }
        .dash-custom-error { color: var(--danger); font-size: .8125rem; }
        .dash-error { color: var(--danger); border: 1px solid var(--danger); border-radius: 8px; padding: 10px 12px; margin-bottom: 14px; }
        .dash section { margin-top: 26px; }
        .dash h2 { margin: 0 0 2px; font-size: 1rem; font-weight: 600; }
        .dash-sub { margin: 0 0 12px; color: var(--text-secondary); font-size: .8125rem; }
        .dash-card { background: var(--bg-card); border: 1px solid var(--border-primary); border-radius: 12px; padding: 16px 18px; }
        .dash-hero { margin-bottom: 12px; }
        .dash-hero-value { font-size: 3rem; line-height: 1.15; font-weight: 600; }
        .dash-tiles { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 12px; }
        .dash-tile-label { font-size: .8125rem; color: var(--text-secondary); }
        .dash-tile-value { font-size: 1.625rem; font-weight: 600; margin-top: 2px; }
        .dash-tile-note { font-size: .8125rem; color: var(--text-secondary); }
        .dash-up { color: var(--success); } .dash-down { color: var(--danger); }
        .dash-mt { margin-top: 12px; }
        .dash-tablewrap { overflow-x: auto; }
        .dash-table { border-collapse: collapse; width: 100%; font-size: .875rem; }
        .dash-table th { text-align: right; font-weight: 500; font-size: .8125rem; color: var(--text-secondary); padding: 6px 10px; border-bottom: 1px solid var(--border-secondary); white-space: nowrap; background: none; }
        .dash-table td { text-align: right; padding: 7px 10px; border-bottom: 1px solid var(--border-primary); font-variant-numeric: tabular-nums; white-space: nowrap; }
        .dash-table th:first-child, .dash-table td:first-child { text-align: left; padding-left: 0; }
        .dash-table tr:last-child td { border-bottom: 0; }
        .dash-total td { font-weight: 600; border-top: 1px solid var(--border-secondary); }
        .dash-barcell { width: 24%; min-width: 110px; padding-right: 0 !important; }
        .dash-bar { height: 10px; min-width: 2px; background: var(--dash-series); border-radius: 0 4px 4px 0; }
        .dash-chart { position: relative; width: 100%; }
        .dash-chart svg { display: block; width: 100%; }
        .dash-chart rect:focus { outline: none; }
        .dash-chart rect:focus-visible { outline: 2px solid var(--primary); outline-offset: -2px; }
        .dash-tip { position: absolute; transform: translate(-50%, -100%); pointer-events: none; white-space: nowrap; text-align: left; line-height: 1.4; background: var(--text-primary); color: var(--bg-card); font-size: .8125rem; padding: 6px 9px; border-radius: 6px; z-index: 2; }
        .dash-details { margin-top: 10px; font-size: .875rem; }
        .dash-details summary { cursor: pointer; color: var(--text-secondary); }
        .dash-daytable { max-height: 320px; overflow-y: auto; }
        .dash-empty { color: var(--text-secondary); padding: 8px 0; }
        .dash-grid2 { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 340px), 1fr)); gap: 0 20px; align-items: start; }
        .dash-hbars { list-style: none; margin: 0; padding: 0; }
        .dash-hbars li { position: relative; display: grid; grid-template-columns: minmax(80px, 34%) 1fr 2.5em; gap: 10px; align-items: center; padding: 5px 0; font-size: .875rem; border-radius: 4px; }
        .dash-hbars li:focus { outline: none; }
        .dash-hbars li:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }
        .dash-hbar-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .dash-hbar-track { height: 14px; }
        .dash-hbar { display: block; height: 100%; min-width: 2px; background: var(--dash-series); border-radius: 0 4px 4px 0; }
        .dash-hbar-muted { background: var(--text-secondary); opacity: .45; }
        .dash-hbar-value { text-align: right; font-variant-numeric: tabular-nums; }
        .dash-hbar-tip { left: 60%; top: 2px; }
        @media (max-width: 560px) { .dash-hero-value { font-size: 2.5rem; } }
      `}</style>
    </DashboardLayout>
  );
};
